/**
 * Polls progress for an active CSV import job.
 *
 * - **Store phase**: polls `csv_import_jobs` by storeJobId (rows saved).
 * - **Scoring / enrichment phase**: polls contacts for ICP score + enrichment
 *   status (existing post-import onboarding path).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ActiveCsvImport } from '@/contexts/CsvImportProgressContext';

const SUCCESSFUL_ENRICHMENT_STATUSES = ['profile_enriched', 'fully_enriched', 'completed'] as const;
const FINISHED_ENRICHMENT_STATUSES = [...SUCCESSFUL_ENRICHMENT_STATUSES, 'failed', 'not_found'] as const;

const TERMINAL_STORE_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
  'rolled_back',
]);

export interface CsvImportProgress {
  /** Store-phase rows processed (denominator = storeTotal). */
  storeProcessed: number;
  storeTotal: number;
  storeFailed: number;
  storeStatus: string | null;
  isStoreDone: boolean;
  hasStorePhase: boolean;

  total: number;
  scored: number;
  enriched: number;
  enrichmentFinished: number;
  enrichTarget: number;
  isScored: boolean;
  isEnriched: boolean;
  /** Fully done (store + scoring/enrichment as applicable). */
  isDone: boolean;
}

const EMPTY_PROGRESS: CsvImportProgress = {
  storeProcessed: 0,
  storeTotal: 0,
  storeFailed: 0,
  storeStatus: null,
  isStoreDone: true,
  hasStorePhase: false,
  total: 0,
  scored: 0,
  enriched: 0,
  enrichmentFinished: 0,
  enrichTarget: 0,
  isScored: false,
  isEnriched: false,
  isDone: false,
};

// We expect the initial enrichment burst to size up to 200 of the top-fit
// contacts. Once enrichment matches that target the pill is "done" — the
// rhythm game will top up further enrichment as the user processes contacts.
const INITIAL_ENRICH_TARGET = 200;

export function useCsvImportProgressPoll(job: ActiveCsvImport | null): CsvImportProgress {
  const hasStore = !!job?.storeJobId;
  const hasContacts = !!job && job.contactIds.length > 0;

  const { data } = useQuery({
    enabled: !!job && (hasStore || hasContacts),
    queryKey: [
      'csv-import-progress',
      job?.startedAt,
      job?.storeJobId,
      job?.totalRows,
      job?.contactIds.length,
      job?.enrichContactIds?.length,
      job?.scoringComplete,
      job?.scoringFailedCount,
      job?.enrichmentComplete,
    ],
    refetchInterval: (q) => {
      const current = q.state.data as CsvImportProgress | undefined;
      if (current?.isDone) return false;
      // Store phase: poll a bit faster so the row counter feels live.
      if (current?.hasStorePhase && !current.isStoreDone) return 2000;
      return 4000;
    },
    queryFn: async (): Promise<CsvImportProgress> => {
      if (!job) return EMPTY_PROGRESS;

      let storeProcessed = 0;
      let storeTotal = job.totalRows ?? 0;
      let storeFailed = 0;
      let storeStatus: string | null = null;
      let isStoreDone = !job.storeJobId;
      const hasStorePhase = !!job.storeJobId;

      if (job.storeJobId) {
        const { data: jobRow, error: jobErr } = await supabase
          .from('csv_import_jobs')
          .select('status, processed_rows, failed_rows, total_rows, stats')
          .eq('id', job.storeJobId)
          .maybeSingle();

        if (!jobErr && jobRow) {
          storeStatus = jobRow.status;
          storeTotal = jobRow.total_rows ?? storeTotal;
          storeProcessed = jobRow.processed_rows ?? 0;
          storeFailed = jobRow.failed_rows ?? 0;
          isStoreDone = TERMINAL_STORE_STATUSES.has(jobRow.status);
          // Prefer stats.new when present for a cleaner "saved" count later.
          void jobRow.stats;
        }
      }

      // No scoring phase (company/deal store-only import).
      if (job.contactIds.length === 0) {
        return {
          storeProcessed,
          storeTotal,
          storeFailed,
          storeStatus,
          isStoreDone,
          hasStorePhase,
          total: 0,
          scored: 0,
          enriched: 0,
          enrichmentFinished: 0,
          enrichTarget: 0,
          isScored: true,
          isEnriched: true,
          isDone: isStoreDone,
        };
      }

      // Wait for store before counting scoring when both phases exist.
      if (hasStorePhase && !isStoreDone) {
        return {
          storeProcessed,
          storeTotal,
          storeFailed,
          storeStatus,
          isStoreDone: false,
          hasStorePhase,
          total: job.contactIds.length,
          scored: 0,
          enriched: 0,
          enrichmentFinished: 0,
          enrichTarget: 0,
          isScored: false,
          isEnriched: false,
          isDone: false,
        };
      }

      const ids = job.contactIds;

      const { count: scoredCount, error: scoredErr } = await supabase
        .from('contacts')
        .select('id', { count: 'exact', head: true })
        .in('id', ids)
        .not('best_icp_score', 'is', null);

      if (scoredErr) {
        return {
          ...EMPTY_PROGRESS,
          storeProcessed,
          storeTotal,
          storeFailed,
          storeStatus,
          isStoreDone,
          hasStorePhase,
          total: ids.length,
          isDone: false,
        };
      }

      const total = ids.length;
      const scored = Math.min(scoredCount ?? 0, total);
      const isScored = job.scoringComplete === true || scored >= total;

      const enrichmentIds = job.enrichContactIds;
      const enrichTarget = enrichmentIds
        ? enrichmentIds.length
        : Math.min(INITIAL_ENRICH_TARGET, total);
      let enriched = 0;
      let enrichmentFinished = 0;

      if (enrichmentIds && enrichmentIds.length > 0) {
        const [{ count: enrichedCount }, { count: finishedCount }] = await Promise.all([
          supabase
            .from('contacts')
            .select('id', { count: 'exact', head: true })
            .in('id', enrichmentIds)
            .in('enrichment_status', SUCCESSFUL_ENRICHMENT_STATUSES as unknown as string[]),
          supabase
            .from('contacts')
            .select('id', { count: 'exact', head: true })
            .in('id', enrichmentIds)
            .in('enrichment_status', FINISHED_ENRICHMENT_STATUSES as unknown as string[]),
        ]);
        enriched = Math.min(enrichedCount ?? 0, enrichTarget);
        enrichmentFinished = Math.min(finishedCount ?? 0, enrichTarget);
      }

      const isEnriched = job.enrichmentComplete === true
        || enrichTarget === 0
        || enrichmentFinished >= enrichTarget;

      return {
        storeProcessed,
        storeTotal,
        storeFailed,
        storeStatus,
        isStoreDone,
        hasStorePhase,
        total,
        scored,
        enriched,
        enrichmentFinished,
        enrichTarget,
        isScored,
        isEnriched,
        isDone: isStoreDone && isScored && isEnriched,
      };
    },
  });

  return data ?? EMPTY_PROGRESS;
}
